/**
 * On-demand device location for check-in / check-out.
 *
 * A position is requested fresh at the moment of the action, with high
 * accuracy, and never served from the browser cache. The previous approach —
 * a low-accuracy fix taken on page load and cached for up to 30 minutes — gave
 * Wi-Fi / cell / IP positions that were routinely 100 m+ off, or showed where
 * the employee was on the way in rather than where they are now.
 */

export interface ILocationFix {
  lat: number;
  long: number;
  /** Radius of uncertainty reported by the device, in metres. */
  accuracy: number | null;
}

/** A fix this precise is good enough — stop waiting for a better one. */
export const LOCATION_TARGET_ACCURACY_METERS = 30;

/** After the first fix, how long to keep listening for a more precise one. */
const SETTLE_AFTER_FIRST_FIX_MS = 2_000;

/**
 * Per-fix timeout for the high-accuracy watch. Passed to the browser rather
 * than run as our own timer because the browser only starts it once location
 * permission is granted, so a user reading the permission prompt is not
 * counted against it.
 */
const HIGH_ACCURACY_TIMEOUT_MS = 15_000;

/**
 * How long to keep waiting after a transient "position unavailable" error.
 * macOS (Chrome and Safari) reports one while Core Location is still
 * acquiring, and a real fix routinely follows a moment later — so it must not
 * end the attempt on its own.
 */
const UNAVAILABLE_GRACE_MS = 10_000;

/** Last-resort guard in case the browser never calls back at all. */
const WATCH_GUARD_MS = 60_000;

const COARSE_FALLBACK_TIMEOUT_MS = 10_000;
const COARSE_FALLBACK_MAX_AGE_MS = 5 * 60_000;

// GeolocationPositionError codes — not referenced through the global so this
// also works where the constructor is not exposed.
const PERMISSION_DENIED = 1;
const TIMEOUT = 3;

export const isLocationPermissionDenied = (error: unknown): boolean =>
  (error as GeolocationPositionError | null)?.code === PERMISSION_DENIED;

const toFix = (position: GeolocationPosition): ILocationFix => ({
  lat: position.coords.latitude,
  long: position.coords.longitude,
  accuracy: Number.isFinite(position.coords.accuracy)
    ? position.coords.accuracy
    : null,
});

/**
 * Watch high-accuracy fixes and resolve with the most precise one: as soon as
 * one meets the target accuracy, or a short settle window after the first fix
 * arrives. A cold GPS usually reports a coarse fix first and tightens over the
 * next second or two, so taking the very first callback would throw away most
 * of the benefit of asking for high accuracy.
 */
const watchBestPosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    let best: GeolocationPosition | null = null;
    let settled = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    let graceTimer: ReturnType<typeof setTimeout> | undefined;
    let lastError: GeolocationPositionError | undefined;

    // Geolocation callbacks are always queued as tasks, never invoked
    // synchronously, so `watchId` and `guardTimer` are assigned before any
    // path can reach `finish`.
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      clearTimeout(settleTimer);
      clearTimeout(graceTimer);
      clearTimeout(guardTimer);
      if (best) resolve(best);
      else reject(error ?? lastError ?? new Error("Unable to determine location"));
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!best || position.coords.accuracy < best.coords.accuracy) {
          best = position;
        }
        if (position.coords.accuracy <= LOCATION_TARGET_ACCURACY_METERS) {
          finish();
          return;
        }
        settleTimer ??= setTimeout(() => finish(), SETTLE_AFTER_FIRST_FIX_MS);
      },
      (error) => {
        // Denial and the browser's own timeout are final; settle with the best
        // fix so far, if any. "Unavailable" is often transient — keep
        // listening for a bounded grace period instead of giving up.
        if (error.code === PERMISSION_DENIED || error.code === TIMEOUT) {
          finish(error);
          return;
        }
        lastError = error;
        graceTimer ??= setTimeout(() => finish(), UNAVAILABLE_GRACE_MS);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: HIGH_ACCURACY_TIMEOUT_MS,
      },
    );

    const guardTimer = setTimeout(() => finish(), WATCH_GUARD_MS);
  });

const getCoarsePosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      maximumAge: COARSE_FALLBACK_MAX_AGE_MS,
      timeout: COARSE_FALLBACK_TIMEOUT_MS,
    }),
  );

/**
 * Get the device's current location, as precisely as it can manage.
 *
 * Rejects when geolocation is unsupported, permission is denied, or no fix at
 * all could be obtained.
 */
export const getCurrentLocation = async (): Promise<ILocationFix> => {
  if (!navigator.geolocation) {
    throw new Error("Geolocation is not supported by this browser");
  }

  try {
    return toFix(await watchBestPosition());
  } catch (error) {
    if (isLocationPermissionDenied(error)) throw error;
    // No high-accuracy fix in time (no GPS lock indoors, or a desktop with
    // location services limited). A coarse fix still carries its accuracy, so
    // the server can weigh it instead of receiving no location at all.
    return toFix(await getCoarsePosition());
  }
};

/**
 * "±118 m" for a fix's accuracy radius, or null when there is none worth
 * showing. Rounded to 5 m — the radius is an estimate, and "±117.6 m" reads as
 * more certain than it is.
 */
export const formatAccuracy = (accuracy?: number | null): string | null =>
  typeof accuracy === "number" && Number.isFinite(accuracy) && accuracy > 0
    ? `±${Math.max(5, Math.round(accuracy / 5) * 5)} m`
    : null;
